import { DatapointStatus, PerformanceDatapoint } from 'owa-analytics';
import { isGlobalFeatureEnabled } from 'owa-feature-flags';
import { getBootCustomData } from 'owa-performance';
import isBusiness from 'owa-session-store/lib/utils/isBusiness';
import type { TraceErrorObject } from 'owa-analytics-types';
import { trace } from 'owa-trace';
import queryPlacesSettings from './queryPlacesSettings';

import type { MailboxInfo } from 'owa-client-types';
import type { PlacesSettings } from './types/PlacesSettings';
import { tryGetTraceObjectErrorFromApolloError } from 'owa-trace/lib/tryGetTraceObjectErrorFromApolloError';

export const defaultPlacesSettings: PlacesSettings = {
    placesWebAppEnabled: false,
    placesPremiumEnabled: false,
    placesPremiumEnabledFromSettings: false,
    placesCopilotEnabled: false,
    placesFinderEnabled: false,
};

/**
 * Retrieve settings from the service.
 * For details, see the README file.
 * If you make changes to the flow, update the README diagram.
 */
export default async function fetchPlacesSettings(
    mailboxInfo: MailboxInfo,
    shouldUseMainThread?: boolean,
    timeoutMS?: number,
    noBatching?: boolean,
    queryDeduplication?: boolean
): Promise<PlacesSettings> {
    const datapoint = new PerformanceDatapoint('FETCH_PLACES_SETTINGS', mailboxInfo);
    datapoint.addCustomData({ shouldUseMainThread, timeoutMS, noBatching, queryDeduplication });
    datapoint.addCustomData({ hostSessionId: getBootCustomData().HostSessionId }); // Include the MetaOS context for better telemetry

    try {
        // Block Consumer accounts or business accounts in regions where places-web-app-enabled is not yet true (ex, GCC/DoD/Gallatin).
        // Once places-web-app-enabled graduates to ALL environments, this check can be removed.
        // See https://exp.microsoft.com/a/feature/98ee59f0-e88b-43b6-b908-b898ef39ed00?workspaceId=272e8f3b-4b43-4bac-9ca8-30f98bb3a3e4

        const isBusinessUser = isBusiness(mailboxInfo);
        const isPlacesWebAppEnabledV1 = isGlobalFeatureEnabled('places-web-app-enabled');
        const isPlacesWebAppEnabledV2 = isGlobalFeatureEnabled('places-web-app-enabled-v2');

        const isPlacesWebAppEnabled = isPlacesWebAppEnabledV1 || isPlacesWebAppEnabledV2;

        if (!isBusinessUser || !isPlacesWebAppEnabled) {
            /** CASE 1, 2 */
            datapoint.addCustomData({
                reason: `isBusinessUser=${isBusinessUser} isPlacesWebAppEnabled=${isPlacesWebAppEnabled}`,
            });
            setPlacesUserSettingUnifiedTelemetryData(datapoint, false /** actionSuccess */);
            return {
                ...defaultPlacesSettings,
                reason: 'Not a business user or places-web-app-enabled is false',
            };
        }

        // Query the settings, asking for EnablePlacesWebApp and PlacesEnabled.
        const { data, error } = await queryPlacesSettings(
            mailboxInfo,
            [
                'Places.EnablePlacesWebApp',
                'Places.PlacesEnabled',
                'Places.PlacesFinderEnabled',
                'IsPlacesPremiumEnabled',
            ],
            shouldUseMainThread,
            timeoutMS,
            noBatching,
            queryDeduplication
        );

        // If we truly failed the network call, throw an error, which allows the application to retry.
        if (error) {
            /** CASE 7 */
            throw error;
        }

        // If we didn't fail the network call but we got no settings, throw an error too.
        // Not sure if a retry would help since this is truly unexpected, but who knows, maybe it could happen due to a server hiccup.
        const rawSettings = data?.batchGetEffectiveSettings?.settings;
        if (!rawSettings) {
            /** CASE 7 */
            datapoint.addCustomData({ noSettings: true });
            throw new Error('No Places Settings data');
        }

        // Map the incoming array to a simple record.
        const settings = rawSettings.reduce(
            (a, setting) => {
                a[setting.key] = !!setting.value.boolValue;
                return a;
            },
            {} as Record<string, boolean>
        );
        datapoint.addCustomData(settings);

        const placesWebAppEnabled = !!settings['Places.EnablePlacesWebApp']; /** CASE 3, 4, 5, 6*/
        const placesPrivatePreviewPremiumEnabled =
            !!settings['Places.PlacesEnabled'] &&
            placesWebAppEnabled; /** CASE 3: Previous Premium Behavior */
        const placesGAPremiumEnabled = !!settings['IsPlacesPremiumEnabled']; /** CASE 4*/
        const placesPremiumEnabledFromSettings =
            placesGAPremiumEnabled || placesPrivatePreviewPremiumEnabled;

        const placesPremiumFreeFlag = isGlobalFeatureEnabled('places-premium-free');
        const placesPremiumEnabled = placesPremiumFreeFlag || placesPremiumEnabledFromSettings;
        datapoint.addCustomData({ placesPremiumFreeFlag });

        const placesFinderEnabled =
            !!settings['Places.PlacesFinderEnabled'] && placesPremiumEnabled;

        setPlacesUserSettingUnifiedTelemetryData(
            datapoint,
            true /** actionSuccess */,
            placesWebAppEnabled,
            placesPremiumEnabledFromSettings,
            false /** placesCopilotEnabled */,
            placesFinderEnabled,
            placesPremiumFreeFlag
        );

        return {
            placesWebAppEnabled,
            placesPremiumEnabled,
            placesPremiumEnabledFromSettings,
            placesCopilotEnabled: false, // TODO: update when we know what to do about copilot
            placesFinderEnabled,
        };
    } catch (err) {
        // Errors resulting from enabling useUnknownInCatchVariables
        // TS2345: Argument of type 'unknown' is not assignable to parameter of type 'Error | undefined'.
        // @ts-expect-error
        const traceError = tryGetTraceObjectErrorFromApolloError(err) as TraceErrorObject;

        datapoint.addCustomData({
            cv: traceError?.correlationVector ?? 'No CV',
        });

        setPlacesUserSettingUnifiedTelemetryData(datapoint, false /** actionSuccess */);
        // Errors resulting from enabling useUnknownInCatchVariables
        datapoint.endWithError(DatapointStatus.ServerError, traceError);
        // Errors resulting from enabling useUnknownInCatchVariables
        // TS2339: Property 'diagnosticInfo' does not exist on type '{}'.
        // @ts-expect-error
        trace.info('fetchPlacesSettings failed: ' + err?.diagnosticInfo);
        throw err;
    } finally {
        if (!datapoint.hasEnded) {
            datapoint.end();
        }
    }
}

function setPlacesUserSettingUnifiedTelemetryData(
    datapoint: PerformanceDatapoint,
    actionSuccess: boolean,
    placesWebAppEnabled?: boolean,
    placesPremiumEnabled?: boolean,
    placesCopilotEnabled?: boolean,
    placesFinderEnabled?: boolean,
    placesPremiumFreeFlag?: boolean
) {
    datapoint.addUnifiedTelemetryData({
        eventName: 'PlacesUserSettings',
        data: {
            ActionSuccess: actionSuccess,
            PlacesWebAppEnabled: placesWebAppEnabled,
            PlacesPremiumEnabled: placesPremiumEnabled,
            PlacesCopilotEnabled: placesCopilotEnabled,
            PlacesFinderEnabled: placesFinderEnabled,
            // PlacesHybridGuidanceEnabled is an obsolete unused field. We temporarily repurpose it
            // to record the places-premium-free flight value during rollout. Clean up after rollout.
            PlacesHybridGuidanceEnabled: placesPremiumFreeFlag,
        },
    });
}
